/**
 * Service Stripe pour SimulReno - Adapté pour Firebase
 */

import { auth } from '../lib/firebase';

export interface CreateCheckoutSessionRequest {
  priceId: string;
  creditsAmount?: number;
  mode: 'subscription' | 'payment';
  successUrl: string;
  cancelUrl: string;
  promoCode?: string;
  estimateId?: string;
  customerEmail?: string;
  customerName?: string;
  userId?: string;
  billingInterval?: 'monthly' | 'annual';
}

export interface CreateCheckoutSessionResponse {
  sessionId: string;
  url: string;
}

export class StripeServiceError extends Error {
  constructor(
    message: string,
    public code: string,
    public statusCode: number = 400,
    public details?: any
  ) {
    super(message);
    this.name = 'StripeServiceError';
  }
}

export class StripeService {
  async createCheckoutSession(request: CreateCheckoutSessionRequest): Promise<CreateCheckoutSessionResponse> {
    try {
      console.log('🔍 [STRIPE_SERVICE] createCheckoutSession called with request:', {
        mode: request.mode,
        priceId: request.priceId,
        creditsAmount: request.creditsAmount
      });
      
      // Récupérer le token Firebase de l'utilisateur connecté
      const user = auth.currentUser;
      let authToken = null;
      
      if (user) {
        try {
          authToken = await user.getIdToken();
          console.log('✅ Firebase auth token retrieved for user:', user.email);
        } catch (error) {
          console.warn('⚠️ Failed to get Firebase auth token:', error);
        }
      }

      const isAuthenticated = !!authToken;
      console.log('🔍 Auth status:', { isAuthenticated, hasToken: !!authToken });

      // Utiliser la fonction Netlify appropriée selon le mode
      const endpoint = request.mode === 'subscription' 
        ? 'payment-create-subscription'
        : 'payment-one-time-payment';
      
      console.log('📡 Using endpoint:', endpoint);

      // Préparer le body selon le mode
      const body = request.mode === 'subscription'
        ? {
            price_id: request.priceId,
            success_url: request.successUrl,
            cancel_url: request.cancelUrl,
            promo_code: request.promoCode || undefined,
            billing_interval: request.billingInterval || 'monthly'
          }
        : {
            credits_amount: request.creditsAmount || 1,
            success_url: request.successUrl,
            cancel_url: request.cancelUrl,
            promo_code: request.promoCode || undefined,
            estimate_id: request.estimateId
          };

      console.log('📤 Sending request body:', JSON.stringify(body, null, 2));

      // Headers conditionnels selon authentification
      const headers = {
        'Content-Type': 'application/json',
        ...(authToken && { 'Authorization': `Bearer ${authToken}` })
      };

      console.log('🔍 Headers prepared:', {
        hasAuth: !!authToken,
        contentType: headers['Content-Type']
      });
      
      const response = await fetch(`/.netlify/functions/${endpoint}`, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
      });

      console.log('📥 Response status:', response.status, response.statusText);

      if (!response.ok) {
        let errorData;
        try {
          const errorText = await response.text();
          if (errorText) {
            errorData = JSON.parse(errorText);
          } else {
            errorData = { error: 'Empty error response' };
          }
          console.error('❌ Error response data:', JSON.stringify(errorData, null, 2));
        } catch (parseError) {
          console.error('❌ Failed to parse error response:', parseError);
          errorData = { error: `HTTP ${response.status}: ${response.statusText}` };
        }

        // Messages d'erreur spécifiques pour les codes promo
        let errorMessage = errorData.error || 'Failed to create checkout session';
        let errorCode = 'CHECKOUT_CREATION_FAILED';

        if (errorData.error && typeof errorData.error === 'string') {
          // Détection erreurs codes promo courantes
          if (errorData.error.toLowerCase().includes('coupon') || 
              errorData.error.toLowerCase().includes('promo') ||
              errorData.error.toLowerCase().includes('discount')) {
            errorCode = 'PROMO_CODE_ERROR';
            errorMessage = `Code promo invalide: ${errorData.error}`;
          }
        }

        throw new StripeServiceError(
          errorMessage,
          errorCode,
          response.status,
          errorData
        );
      }

      let responseData;
      try {
        const responseText = await response.text();
        if (!responseText || responseText.trim() === '') {
          throw new Error('Empty response from server');
        }
        responseData = JSON.parse(responseText);
        console.log('✅ Success response data:', JSON.stringify(responseData, null, 2));
      } catch (parseError) {
        console.error('❌ Failed to parse success response:', parseError);
        throw new StripeServiceError(
          'Invalid response from payment service',
          'INVALID_RESPONSE',
          500
        );
      }

      const result = {
        sessionId: responseData.session_id || responseData.sessionId || '',
        url: responseData.checkout_url || responseData.url || '',
      };

      console.log('🎉 Checkout session created successfully:', result);
      return result;

    } catch (error) {
      console.error('❌ Error in createCheckoutSession:', error);
      
      if (error instanceof StripeServiceError) {
        throw error;
      }
      
      // Erreur inattendue
      throw new StripeServiceError(
        `Unexpected error: ${error instanceof Error ? error.message : 'Unknown error'}`,
        'UNEXPECTED_ERROR',
        500,
        error
      );
    }
  }

  /**
   * Affichage du statut d'abonnement
   */
  getSubscriptionStatusDisplay(status: string): { label: string; color: string } {
    const statusMap: Record<string, { label: string; color: string }> = {
      'active': { label: 'Actif', color: 'green' },
      'trialing': { label: 'Période d\'essai', color: 'blue' },
      'past_due': { label: 'Paiement en retard', color: 'orange' },
      'canceled': { label: 'Annulé', color: 'red' },
      'unpaid': { label: 'Impayé', color: 'red' },
      'incomplete': { label: 'Incomplet', color: 'yellow' },
      'incomplete_expired': { label: 'Expiré', color: 'red' },
      'paused': { label: 'En pause', color: 'gray' },
      'not_started': { label: 'Non démarré', color: 'gray' },
    };

    return statusMap[status] || { label: status, color: 'gray' };
  }

  /**
   * Formatage de date
   */
  formatDate = (timestamp: number): string => {
    // Vérifier si timestamp est en secondes (format Stripe) ou millisecondes
    const date = timestamp > 9999999999 ? new Date(timestamp) : new Date(timestamp * 1000);
    return date.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  /**
   * Récupérer les données d'abonnement de l'utilisateur
   */
  getUserSubscription = async (): Promise<any> => {
    try {
      const user = auth.currentUser;
      if (!user) {
        console.warn('No authenticated user found for getUserSubscription');
        return null;
      }

      const authToken = await user.getIdToken();

      const response = await fetch('/.netlify/functions/stripe-service', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'getUserSubscription',
          authToken
        }),
      });

      if (!response.ok) {
        console.error('Get subscription error:', response.status);
        return null;
      }

      let data;
      try {
        const responseText = await response.text();
        if (!responseText) {
          console.warn('Empty response for getUserSubscription');
          return null;
        }
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error('Failed to parse getUserSubscription response:', parseError);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Get user subscription error:', error);
      return null;
    }
  };

  /**
   * Vérifier si l'utilisateur a un abonnement actif
   */
  hasActiveSubscription = async (): Promise<boolean> => {
    try {
      const user = auth.currentUser;
      if (!user) {
        console.warn('No authenticated user found for hasActiveSubscription');
        return false;
      }

      const authToken = await user.getIdToken();

      // Appeler la fonction Netlify
      const response = await fetch('/.netlify/functions/stripe-service', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'hasActiveSubscription',
          authToken
        }),
      });

      if (!response.ok) {
        console.error('Check subscription status error:', response.status);
        return false;
      }

      let hasActive;
      try {
        const responseText = await response.text();
        if (!responseText) {
          console.warn('Empty response for hasActiveSubscription');
          return false;
        }
        hasActive = JSON.parse(responseText);
      } catch (parseError) {
        console.error('Failed to parse hasActiveSubscription response:', parseError);
        return false;
      }

      return hasActive;
    } catch (error) {
      console.error('Check active subscription error:', error);
      return false;
    }
  };

  /**
   * Récupérer les informations de crédits de l'utilisateur
   */
  getUserCreditsInfo = async (): Promise<{ hasCredits: boolean; isUnlimited: boolean; credits: number } | null> => {
    try {
      const user = auth.currentUser;
      if (!user) {
        console.warn('🔍 [getUserCreditsInfo] No authenticated user found');
        return { hasCredits: false, isUnlimited: false, credits: 0 };
      }

      console.log('🔍 [getUserCreditsInfo] Starting credit check for user:', user.uid);
      const authToken = await user.getIdToken();
      console.log('🔍 [getUserCreditsInfo] Auth token obtained');

      // Appeler la fonction Netlify
      const response = await fetch('/.netlify/functions/stripe-service', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'getUserCreditsInfo',
          authToken
        }),
      });

      console.log('🔍 [getUserCreditsInfo] Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        // En dev local, 404 est normal (Netlify Functions non disponibles)
        if (response.status === 404) {
          console.warn('⚠️ [getUserCreditsInfo] Netlify Functions not available (dev mode), will use local profile');
        } else {
          console.error('❌ [getUserCreditsInfo] Server error:', response.status, errorText);
        }
        // Retourner null pour signaler une erreur plutôt que de bloquer l'utilisateur
        return null;
      }

      let data;
      try {
        const responseText = await response.text();
        console.log('🔍 [getUserCreditsInfo] Response text:', responseText);

        if (!responseText) {
          console.warn('⚠️ [getUserCreditsInfo] Empty response from server');
          return null;
        }
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error('❌ [getUserCreditsInfo] Failed to parse response:', parseError);
        return null;
      }

      console.log('✅ [getUserCreditsInfo] Credits info retrieved:', {
        hasCredits: data.hasCredits,
        isUnlimited: data.isUnlimited,
        credits: data.credits
      });

      return data;
    } catch (error) {
      console.error('❌ [getUserCreditsInfo] Unexpected error:', error);
      // Retourner null pour signaler une erreur plutôt que de bloquer l'utilisateur
      return null;
    }
  };

  /**
   * Accéder au portail client Stripe - VERSION CORRIGÉE
   */
  accessCustomerPortal = async (): Promise<string> => {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new StripeServiceError(
          'Authentication required',
          'AUTH_REQUIRED',
          401
        );
      }

      const authToken = await user.getIdToken();

      console.log('🔍 Calling payment-customer-portal function...');

      // Appeler la fonction Netlify
      const response = await fetch('/.netlify/functions/payment-customer-portal', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        }
      });

      console.log('📥 Response status:', response.status, response.statusText);

      // CORRECTION: Vérifier d'abord si la réponse est OK
      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        let errorDetails = null;
        
        // Essayer de lire l'erreur JSON si possible
        try {
          const errorText = await response.text();
          console.log('📄 Error response text:', errorText);
          
          if (errorText && errorText.trim() !== '') {
            const errorData = JSON.parse(errorText);
            errorMessage = errorData.error || errorMessage;
            errorDetails = errorData;
          }
        } catch (parseError) {
          console.warn('Failed to parse error response:', parseError);
          // Si on ne peut pas parser l'erreur, garder le message HTTP
        }
        
        throw new StripeServiceError(
          errorMessage,
          'CUSTOMER_PORTAL_ACCESS_FAILED',
          response.status,
          errorDetails
        );
      }

      // CORRECTION: Vérifier que la réponse n'est pas vide avant de parser
      const responseText = await response.text();
      console.log('📄 Success response text:', responseText);
      
      if (!responseText || responseText.trim() === '') {
        throw new StripeServiceError(
          'Empty response from server',
          'EMPTY_RESPONSE',
          500
        );
      }

      // Maintenant parser le JSON en sécurité
      let data;
      try {
        data = JSON.parse(responseText);
        console.log('✅ Parsed response data:', data);
      } catch (parseError) {
        console.error('JSON parse error:', parseError);
        throw new StripeServiceError(
          'Invalid JSON response from server',
          'INVALID_JSON',
          500,
          { responseText, parseError: parseError.message }
        );
      }
      
      if (!data.portal_url) {
        throw new StripeServiceError(
          'Portal URL not found in response',
          'INVALID_RESPONSE_FORMAT',
          500,
          data
        );
      }
      
      console.log('🎉 Portal URL retrieved successfully:', data.portal_url);
      return data.portal_url;
      
    } catch (error) {
      console.error('Customer portal access error:', error);
      
      // Re-throw StripeServiceError as-is
      if (error instanceof StripeServiceError) {
        throw error;
      }
      
      // Wrap other errors
      throw new StripeServiceError(
        `Unexpected error: ${error instanceof Error ? error.message : 'Unknown error'}`,
        'UNEXPECTED_ERROR',
        500,
        error
      );
    }
  };
}

// Export singleton instance
export const stripeService = new StripeService();
export default stripeService;